import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://medialabs.ph';
const DEFAULT_OG_IMAGE = '/medialabs-preview-v3.png';
// Optional Google Search Console verification. When this env var is set at build
// time, a <meta name="google-site-verification"> tag is emitted on every page.
// The domain is already verified in production, so this stays empty by default.
const GOOGLE_SITE_VERIFICATION = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || '';

/**
 * Serialize a JSON-LD object safely for embedding inside an HTML <script> tag.
 * Escaping "<" prevents a stray "</script>" in any string field from prematurely
 * closing the tag (a common XSS/parse hazard with inline JSON-LD).
 */
const toJsonLd = (data) => JSON.stringify(data).replace(/</g, '\\u003c');

/**
 * Reusable SEO head component using react-helmet-async.
 * Provides per-page title, description, OG overrides, optional JSON-LD structured
 * data, and an optional Search Console verification tag.
 */
export const SEOHead = ({
  title = 'MediaLabs | V1 Native Apps & Websites in Under 15 Days',
  description = 'Still carrying that million-dollar app idea in your head? We build the tech for non-techy business owners. No jargon, just great products.',
  path = '/',
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  jsonLd = null,
}) => {
  const fullUrl = `${SITE_URL}${path}`;
  const imageUrl = ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`;
  // Support one schema object or an array of schema objects.
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {GOOGLE_SITE_VERIFICATION && (
        <meta name="google-site-verification" content={GOOGLE_SITE_VERIFICATION} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Structured data (JSON-LD) — rendered client-side for Google's rendering
          crawler. Static/non-JS crawlers get the same schema baked into the raw
          HTML by scripts/prerender-meta.js at build time. */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {toJsonLd(schema)}
        </script>
      ))}
    </Helmet>
  );
};
